/**
 * KarroFin Admin — module-local reactive state.
 *
 * Deliberately NOT a Vuex module. Registering one would mean editing
 * src/store/index.js, which is shared legacy code; Vue.observable gives the
 * same reactivity for the little state this module needs (a product-type
 * switch and the cached filter lookups) with zero legacy footprint.
 */
import Vue from "vue";
import { PRODUCT_FILTER_ALL, DEFAULT_RANGE_DAYS } from "./constants";
import { DEFAULT_RANGE_DAYS as CFG_RANGE_DAYS } from "./config";

const RANGE_DAYS = CFG_RANGE_DAYS || DEFAULT_RANGE_DAYS || 30;

function ymd(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** [from, to] covering the last RANGE_DAYS days, inclusive of today. */
export function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - RANGE_DAYS);
  return [ymd(from), ymd(to)];
}

/**
 * A product read from a URL query, or the first allowed value when it is
 * missing or not one this screen accepts.
 *
 * The allowed list is passed in rather than assumed, because the Dashboard
 * accepts "All" and no other screen does -- one shared helper only works if
 * each caller says what it will take.
 */
export function productFromQuery(value, allowed) {
  return allowed.indexOf(value) !== -1 ? value : allowed[0];
}

const state = Vue.observable({
  /**
   * All | Personal Loan | Business Loan.
   * Offers does not use this: it switches between the two products outright
   * and keeps that choice local, because "All" has no button to live on.
   */
  product_type: PRODUCT_FILTER_ALL,
  /** [from, to] in yyyy-mm-dd, shared as the default across screens. */
  date_range: defaultDateRange(),
  /** Distinct UTM values, fetched once and reused by every filter bar. */
  utm_options: null,
  /** Offer Management filter dropdowns, fetched once. */
  filter_options: null,
});

export default {
  state,

  get productType() {
    return state.product_type;
  },
  setProductType(value) {
    state.product_type = value;
  },

  get dateRange() {
    return state.date_range;
  },
  /**
   * [from, to], or [] for "all dates".
   *
   * An empty range is kept, not swapped for the default: clearing the date box
   * sends [] here, and putting the last 30 days straight back meant a cleared
   * box still sent from_date/to_date -- so a search on Offer Management or User
   * Management could not reach a customer who signed up earlier. Only a missing
   * or malformed value falls back to the default.
   */
  setDateRange(value) {
    if (Array.isArray(value) && (value.length === 2 || value.length === 0)) {
      state.date_range = value;
    } else {
      state.date_range = defaultDateRange();
    }
  },

  get utmOptions() {
    return state.utm_options;
  },
  setUtmOptions(value) {
    state.utm_options = value;
  },

  get filterOptions() {
    return state.filter_options;
  },
  setFilterOptions(value) {
    state.filter_options = value;
  },

  /**
   * Product type as the backend expects it: the UI-only "All" becomes null so
   * callers can spread this straight into request params.
   */
  productTypeParam() {
    return state.product_type === PRODUCT_FILTER_ALL ? null : state.product_type;
  },
};
